"use client";

import { c as _c } from "react/compiler-runtime";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@workspace/ui/components/collapsible";
import { cn } from "@workspace/ui/lib/utils";
import { ChevronDownIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { createContext, memo, useContext, useEffect, useRef, useState } from "react";
import { Streamdown } from "streamdown";
import { Shimmer } from "./shimmer";
type ReasoningContextValue = {
  isStreaming: boolean;
  /**
   * Whether the collapsed-height "rolling window" cap should apply. Stays true
   * from streaming start through the auto-close (and its collapse animation),
   * and only lifts once the user manually re-opens the block — so the content
   * never briefly flashes to full height on completion.
   */
  isRolling: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number | undefined;
  hasContent: boolean;
};
const ReasoningContext = createContext<ReasoningContextValue | null>(null);
export const useReasoning = () => {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error("Reasoning components must be used within Reasoning");
  }
  return context;
};
export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
  hasContent?: boolean;
};
const AUTO_CLOSE_DELAY = 1000;
const MS_IN_S = 1000;
export const Reasoning = memo(t0 => {
  const $ = _c(61);
  let children;
  let className;
  let durationProp;
  let onOpenChange;
  let open;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      isStreaming: t1,
      open,
      defaultOpen: t2,
      onOpenChange,
      duration: durationProp,
      hasContent: t3,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = durationProp;
    $[4] = onOpenChange;
    $[5] = open;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    children = $[1];
    className = $[2];
    durationProp = $[3];
    onOpenChange = $[4];
    open = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  const isStreaming = t1 === undefined ? false : t1;
  const defaultOpen = t2 === undefined ? true : t2;
  const hasContent = t3 === undefined ? true : t3;
  const t4 = defaultOpen && isStreaming;
  let t5;
  if ($[10] !== onOpenChange || $[11] !== open || $[12] !== t4) {
    t5 = {
      prop: open,
      defaultProp: t4,
      onChange: onOpenChange
    };
    $[10] = onOpenChange;
    $[11] = open;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const [isOpen, setIsOpen] = useControllableState(t5);
  let t6;
  if ($[14] !== durationProp) {
    t6 = {
      prop: durationProp,
      defaultProp: undefined
    };
    $[14] = durationProp;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const [duration, setDuration] = useControllableState(t6);
  const [hasAutoClosed, setHasAutoClosed] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [hasStreamed, setHasStreamed] = useState(false);
  const [userExpanded, setUserExpanded] = useState(false);
  const [startTime, setStartTime] = useState(null);
  let t7;
  let t8;
  if ($[16] !== hasStreamed || $[17] !== isStreaming) {
    t7 = () => {
      if (isStreaming && !hasStreamed) {
        setHasStreamed(true);
      }
    };
    t8 = [isStreaming, hasStreamed];
    $[16] = hasStreamed;
    $[17] = isStreaming;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  const isRolling = hasStreamed && !userExpanded;
  let t10;
  let t9;
  if ($[20] !== isStreaming || $[21] !== setDuration || $[22] !== startTime) {
    t9 = () => {
      if (isStreaming) {
        if (startTime === null) {
          setStartTime(Date.now());
        }
      } else {
        if (startTime !== null) {
          setDuration(Math.ceil((Date.now() - startTime) / MS_IN_S));
          setStartTime(null);
        }
      }
    };
    t10 = [isStreaming, startTime, setDuration];
    $[20] = isStreaming;
    $[21] = setDuration;
    $[22] = startTime;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[25] !== defaultOpen || $[26] !== hasAutoOpened || $[27] !== isStreaming || $[28] !== setIsOpen) {
    t11 = () => {
      if (defaultOpen && isStreaming && !hasAutoOpened) {
        setIsOpen(true);
        setHasAutoOpened(true);
      }
    };
    t12 = [isStreaming, defaultOpen, hasAutoOpened, setIsOpen];
    $[25] = defaultOpen;
    $[26] = hasAutoOpened;
    $[27] = isStreaming;
    $[28] = setIsOpen;
    $[29] = t11;
    $[30] = t12;
  } else {
    t11 = $[29];
    t12 = $[30];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[31] !== defaultOpen || $[32] !== hasAutoClosed || $[33] !== hasStreamed || $[34] !== isOpen || $[35] !== isStreaming || $[36] !== setIsOpen || $[37] !== userExpanded) {
    t13 = () => {
      if (defaultOpen && hasStreamed && !isStreaming && isOpen && !hasAutoClosed && !userExpanded) {
        const timer = setTimeout(() => {
          setIsOpen(false);
          setHasAutoClosed(true);
        }, AUTO_CLOSE_DELAY);
        return () => clearTimeout(timer);
      }
    };
    t14 = [isStreaming, isOpen, defaultOpen, setIsOpen, hasAutoClosed, hasStreamed, userExpanded];
    $[31] = defaultOpen;
    $[32] = hasAutoClosed;
    $[33] = hasStreamed;
    $[34] = isOpen;
    $[35] = isStreaming;
    $[36] = setIsOpen;
    $[37] = userExpanded;
    $[38] = t13;
    $[39] = t14;
  } else {
    t13 = $[38];
    t14 = $[39];
  }
  useEffect(t13, t14);
  let t15;
  if ($[40] !== isStreaming || $[41] !== setIsOpen) {
    t15 = newOpen => {
      if (newOpen && !isStreaming) {
        setUserExpanded(true);
      }
      setIsOpen(newOpen);
    };
    $[40] = isStreaming;
    $[41] = setIsOpen;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const handleOpenChange = t15;
  let t16;
  if ($[43] !== duration || $[44] !== hasContent || $[45] !== isOpen || $[46] !== isRolling || $[47] !== isStreaming || $[48] !== setIsOpen) {
    t16 = {
      isStreaming,
      isRolling,
      isOpen,
      setIsOpen,
      duration,
      hasContent
    };
    $[43] = duration;
    $[44] = hasContent;
    $[45] = isOpen;
    $[46] = isRolling;
    $[47] = isStreaming;
    $[48] = setIsOpen;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== className) {
    t17 = cn("not-prose", className);
    $[50] = className;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== children || $[53] !== handleOpenChange || $[54] !== isOpen || $[55] !== props || $[56] !== t17) {
    t18 = <Collapsible className={t17} onOpenChange={handleOpenChange} open={isOpen} {...props}>{children}</Collapsible>;
    $[52] = children;
    $[53] = handleOpenChange;
    $[54] = isOpen;
    $[55] = props;
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t16 || $[59] !== t18) {
    t19 = <ReasoningContext.Provider value={t16}>{t18}</ReasoningContext.Provider>;
    $[58] = t16;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  return t19;
});
export type ReasoningTriggerProps = ComponentProps<typeof CollapsibleTrigger> & {
  getThinkingMessage?: (isStreaming: boolean, duration?: number) => ReactNode;
};
const defaultGetThinkingMessage = (isStreaming: boolean, duration?: number) => {
  if (isStreaming || duration === 0) {
    return <Shimmer duration={1}>Thinking...</Shimmer>;
  }
  if (duration === undefined) {
    return <p>Thought for a few seconds</p>;
  }
  return <p>Thought for {duration} seconds</p>;
};
export const ReasoningTrigger = memo(t0 => {
  const $ = _c(18);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      getThinkingMessage: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const getThinkingMessage = t1 === undefined ? defaultGetThinkingMessage : t1;
  const {
    isStreaming,
    isOpen,
    duration,
    hasContent
  } = useReasoning();
  let t2;
  if ($[5] !== className) {
    t2 = cn("flex items-center gap-1.5 text-muted-foreground text-xs transition-colors hover:text-foreground", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== duration || $[9] !== getThinkingMessage || $[10] !== hasContent || $[11] !== isOpen || $[12] !== isStreaming) {
    t3 = children ?? <>{getThinkingMessage(isStreaming, duration)}{hasContent && <ChevronDownIcon className={cn("size-3 transition-transform", isOpen ? "rotate-180" : "rotate-0")} />}</>;
    $[7] = children;
    $[8] = duration;
    $[9] = getThinkingMessage;
    $[10] = hasContent;
    $[11] = isOpen;
    $[12] = isStreaming;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== props || $[15] !== t2 || $[16] !== t3) {
    t4 = <CollapsibleTrigger className={t2} {...props}>{t3}</CollapsibleTrigger>;
    $[14] = props;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
});
export type ReasoningContentProps = ComponentProps<typeof CollapsibleContent> & {
  children: string;
  /**
   * While streaming, cap the visible reasoning to this many lines and keep the
   * latest chunks pinned to the bottom (older lines roll off the top behind a
   * fade). Once streaming ends the cap is lifted so a manual re-open shows the
   * full thought. Set to `0` to disable the rolling window.
   */
  streamingMaxLines?: number;
};

// Deterministic per-line height so `maxHeight` maps to a whole number of lines.
// Matches the `leading-6` (1.5rem) applied to the scroll container below.
const STREAM_LINE_HEIGHT_REM = 1.5;
const DEFAULT_STREAMING_MAX_LINES = 5;
export const ReasoningContent = memo(t0 => {
  const $ = _c(32);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      streamingMaxLines: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const streamingMaxLines = t1 === undefined ? DEFAULT_STREAMING_MAX_LINES : t1;
  const {
    isStreaming,
    isRolling
  } = useReasoning();
  const scrollRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const applyCap = isRolling && streamingMaxLines > 0;
  let t2;
  if ($[5] !== isRolling || $[6] !== isStreaming || $[7] !== streamingMaxLines) {
    t2 = () => {
      if (!(isRolling && streamingMaxLines > 0)) {
        setIsOverflowing(false);
        return;
      }
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      if (isStreaming) {
        el.scrollTop = el.scrollHeight;
      }
      setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
    };
    $[5] = isRolling;
    $[6] = isStreaming;
    $[7] = streamingMaxLines;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== isRolling || $[11] !== isStreaming || $[12] !== streamingMaxLines) {
    t3 = [children, isRolling, streamingMaxLines, isStreaming];
    $[9] = children;
    $[10] = isRolling;
    $[11] = isStreaming;
    $[12] = streamingMaxLines;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  useEffect(t2, t3);
  let t4;
  if ($[14] !== className) {
    t4 = cn("mt-4 text-sm", "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-muted-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in", className);
    $[14] = className;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = applyCap && "overflow-hidden";
  const t6 = applyCap && isOverflowing && "[mask-image:linear-gradient(to_bottom,transparent,#000_1.5rem)]";
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = cn("leading-6", t5, t6);
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== applyCap || $[20] !== streamingMaxLines) {
    t8 = applyCap ? {
      maxHeight: `${streamingMaxLines * STREAM_LINE_HEIGHT_REM}rem`
    } : undefined;
    $[19] = applyCap;
    $[20] = streamingMaxLines;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== children) {
    t9 = <Streamdown>{children}</Streamdown>;
    $[22] = children;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className={t7} ref={scrollRef} style={t8}>{t9}</div>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== props || $[29] !== t10 || $[30] !== t4) {
    t11 = <CollapsibleContent className={t4} {...props}>{t10}</CollapsibleContent>;
    $[28] = props;
    $[29] = t10;
    $[30] = t4;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
});
Reasoning.displayName = "Reasoning";
ReasoningTrigger.displayName = "ReasoningTrigger";
ReasoningContent.displayName = "ReasoningContent";